import React, { useState, useEffect } from 'react';
import { Button } from '@material-ui/core';
import {
  KeyboardArrowDown,
  KeyboardArrowRight,
  ArrowUpward,
  ArrowDownward,
} from '@material-ui/icons';
import {
  CostCenterRecord,
  UserTableFilters,
  UsersHistoryDetails,
  UsageRecord,
  CostCenterResource,
} from '../../interfaces/types';
import {
  formatCurrencyAbbreviated,
  monthNames,
  formatNumber,
  generateCSV,
} from '../../utils/utils';
import Pagination from '../pagination/Pagination';
import { EmeraldSearch } from '@emerald-react/search';

const SkeletonLoader: React.FC = () => {
  return (
    <>
      <div className="cost-center-summary-grid cost-center-summary-grid--detail-users">
        {[...Array(4)].map((_, idx) => (
          <div key={idx} className="cost-center-summary-card">
            <div
              className="skeleton-loader"
              style={{ width: '120px', height: '16px', marginBottom: '8px' }}
            />
            <div
              className="skeleton-loader"
              style={{ width: '80px', height: '32px' }}
            />
          </div>
        ))}
      </div>

      <div className="cost-center-table-shell">
        <table className="cost-center-table">
          <thead>
            <tr>
              <th style={{ width: '40px' }}></th>
              <th>Username</th>
              <th>Email</th>
              <th>Excess spend ($)</th>
              <th>Actual spend ($)</th>
            </tr>
          </thead>
          <tbody>
            {[...Array(2)].map((_, idx) => (
              <tr key={idx}>
                <td style={{ padding: '8px' }}>
                  <div
                    className="skeleton-loader"
                    style={{ width: '20px', height: '20px' }}
                  />
                </td>
                <td>
                  <div
                    className="skeleton-loader"
                    style={{ width: '150px', height: '16px' }}
                  />
                </td>
                <td>
                  <div
                    className="skeleton-loader"
                    style={{ width: '180px', height: '16px' }}
                  />
                </td>
                <td>
                  <div
                    className="skeleton-loader"
                    style={{ width: '60px', height: '16px' }}
                  />
                </td>
                <td>
                  <div
                    className="skeleton-loader"
                    style={{ width: '60px', height: '16px' }}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
};

interface UsersSectionProps {
  selectedCostCenter: CostCenterRecord;
  displayPeriod: string;
  showUsersFilters: boolean;
  userTableFilters: UserTableFilters;
  activeUserFilterCount: number;
  usersPage: number;
  usersFiltersDropdownRef: React.RefObject<HTMLDivElement | null>;
  onToggleShowUsersFilters: () => void;
  onToggleUserTableFilter: (filterKey: keyof UserTableFilters) => void;
  onClearUserFilters: () => void;
  onExportCSV: () => void;
  onPageChange: (newPage: number) => void;
  isLoading?: boolean;
  usersData: UsersHistoryDetails;
}

export const UsersSection: React.FC<UsersSectionProps> = ({
  selectedCostCenter,
  showUsersFilters,
  userTableFilters,
  activeUserFilterCount,
  //usersPage,
  usersFiltersDropdownRef,
  onToggleShowUsersFilters,
  onToggleUserTableFilter,
  onClearUserFilters,
  //onExportCSV,
  //onPageChange,
  isLoading = false,
  usersData,
}) => {
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
  const [filteredUsersData, setFilteredUsersData] =
    useState<UsersHistoryDetails>({} as UsersHistoryDetails);
  const [sortColumn, setSortColumn] = useState<
    'username' | 'email' | 'excessSpend' | 'actualSpend' | null
  >(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [usersPage, setUsersPage] = useState(0);
  const [currentPageData, setCurrentPageData] = useState<UsageRecord[]>([]);

  // Debounce search query
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 300); // 300ms delay

    return () => clearTimeout(timeoutId);
  }, [searchQuery]);

  useEffect(() => {
    let filteredUsers: UsageRecord[] = [];
    let totalGrossAmount = 0;
    let totalNetAmount = 0;
    let anyFilterValidated = false;

    if (!usersData?.usersData) {
      setFilteredUsersData({
        additionalInfo: {
          totalGrossAmount,
          totalNetAmount,
          noUsageDetailsUsers: [],
        },
        usersData: filteredUsers,
      });
    } else {
      if (debouncedSearchQuery.trim() !== '') {
        anyFilterValidated = true;
        filteredUsers = usersData?.usersData?.filter(
          (user: UsageRecord) =>
            user.user
              .toLowerCase()
              .includes(debouncedSearchQuery.toLowerCase()) ||
            user.email
              .toLowerCase()
              .includes(debouncedSearchQuery.toLowerCase()),
        );
      } else {
        filteredUsers =
          usersData?.usersData?.map((user: UsageRecord) => user) || [];
      }

      if (
        userTableFilters.activeNoCostCenter ||
        //userTableFilters.bothSources ||
        userTableFilters.hideNoUsage ||
        //userTableFilters.noCostCenter ||
        userTableFilters.noEmail
      ) {
        anyFilterValidated = true;
        if (userTableFilters.activeNoCostCenter) {
          filteredUsers = (
            usersData?.additionalInfo?.noUsageDetailsUsers || []
          ).map(
            (each: CostCenterResource) =>
              ({
                email: each.email,
                month: selectedCostCenter.month,
                source: selectedCostCenter.source,
                usageItems: [],
                user: each.name,
                year: selectedCostCenter.year,
                totalGrossAmount: 0,
                totalNetAmount: 0,
              } as UsageRecord),
          );
        }
        filteredUsers = filteredUsers.filter((user: UsageRecord) => {
          if (userTableFilters.hideNoUsage && user.totalGrossAmount) {
            return true;
          }
          if (userTableFilters.noEmail && !user.email) {
            return true;
          }
          return userTableFilters.hideNoUsage || userTableFilters.noEmail
            ? false
            : true;
        });
      }
      if (anyFilterValidated) {
        filteredUsers = filteredUsers.map((user: UsageRecord) => {
          totalGrossAmount += user.totalGrossAmount || 0;
          totalNetAmount += user.totalNetAmount || 0;
          return user;
        });
        setFilteredUsersData({
          additionalInfo: {
            totalGrossAmount,
            totalNetAmount,
            noUsageDetailsUsers:
              usersData?.additionalInfo?.noUsageDetailsUsers || [],
          },
          usersData: filteredUsers,
        });
      } else {
        setFilteredUsersData(usersData);
      }
    }
  }, [debouncedSearchQuery, usersData, userTableFilters]);

  useEffect(() => {
    if (!showUsersFilters) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (
        usersFiltersDropdownRef.current &&
        !usersFiltersDropdownRef.current.contains(event.target as Node)
      ) {
        onToggleShowUsersFilters();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showUsersFilters, onToggleShowUsersFilters, usersFiltersDropdownRef]);

  const toggleRow = (index: number) => {
    setExpandedRows(prev => {
      const newSet = new Set(prev);
      if (newSet.has(index)) {
        newSet.delete(index);
      } else {
        newSet.add(index);
      }
      return newSet;
    });
  };

  const handleSort = (
    column: 'username' | 'email' | 'excessSpend' | 'actualSpend',
  ) => {
    if (sortColumn === column) {
      // Toggle direction if clicking the same column
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // Default to ascending for new column
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  useEffect(() => {
    const renderedData: UsageRecord[] = filteredUsersData?.usersData?.length
      ? getSortedData(filteredUsersData.usersData)
      : [];
    setCurrentPageData(renderedData);
  }, [filteredUsersData, sortColumn, sortDirection, usersPage, rowsPerPage]);

  const getSortedData = (data: UsageRecord[]) => {
    let sortedData: UsageRecord[];
    if (!sortColumn || !data) {
      sortedData = data;
    } else {
      sortedData = [...data].sort((a, b) => {
        let aValue: string | number;
        let bValue: string | number;

        switch (sortColumn) {
          case 'username':
            aValue = a.user?.toLowerCase() || '';
            bValue = b.user?.toLowerCase() || '';
            break;
          case 'email':
            aValue = a.email?.toLowerCase() || '';
            bValue = b.email?.toLowerCase() || '';
            break;
          case 'excessSpend':
            aValue = a.totalNetAmount || 0;
            bValue = b.totalNetAmount || 0;
            break;
          case 'actualSpend':
            aValue = a.totalGrossAmount || 0;
            bValue = b.totalGrossAmount || 0;
            break;
          default:
            return 0;
        }

        if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
        if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
        return 0;
      });
    }
    const startingIndex = usersPage * rowsPerPage;
    const endingIndex = startingIndex + rowsPerPage;
    return sortedData?.length
      ? sortedData.slice(startingIndex, endingIndex)
      : [];
  };

  const renderSortIcon = (
    column: 'username' | 'email' | 'excessSpend' | 'actualSpend',
  ) => {
    if (sortColumn !== column) return null;
    return sortDirection === 'asc' ? (
      <ArrowUpward style={{ fontSize: '16px', marginLeft: '4px' }} />
    ) : (
      <ArrowDownward style={{ fontSize: '16px', marginLeft: '4px' }} />
    );
  };

  const handleExportCSV = () => {
    if (!filteredUsersData?.usersData?.length) return;

    // Get all sorted data (not just current page)
    let allSortedData = [...filteredUsersData.usersData];

    if (sortColumn) {
      allSortedData.sort((a, b) => {
        let aValue: string | number;
        let bValue: string | number;

        switch (sortColumn) {
          case 'username':
            aValue = a.user?.toLowerCase() || '';
            bValue = b.user?.toLowerCase() || '';
            break;
          case 'email':
            aValue = a.email?.toLowerCase() || '';
            bValue = b.email?.toLowerCase() || '';
            break;
          case 'excessSpend':
            aValue = a.totalNetAmount || 0;
            bValue = b.totalNetAmount || 0;
            break;
          case 'actualSpend':
            aValue = a.totalGrossAmount || 0;
            bValue = b.totalGrossAmount || 0;
            break;
          default:
            return 0;
        }

        if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
        if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
        return 0;
      });
    }

    const headers = [
      'Type',
      'Username',
      'Email',
      'Model',
      'Utilised AIC',
      'Actual Spend ($)',
      'Excess Spend ($)',
      'Overuse AIC',
    ];

    const rows: string[][] = [];

    allSortedData.forEach(user => {
      // Add main user row
      rows.push([
        'User',
        user.user || '',
        user.email || '',
        '',
        '',
        (user.totalGrossAmount || 0).toFixed(2),
        (user.totalNetAmount || 0).toFixed(2),
        '',
      ]);

      // Add usage items if available
      if (user.usageItems && user.usageItems.length > 0) {
        user.usageItems.forEach(item => {
          rows.push([
            'Usage Detail',
            '',
            '',
            item.model || '',
            item.grossQuantity?.toLocaleString() || '0',
            item.grossAmount?.toFixed(2) || '0.00',
            item.netAmount?.toFixed(2) || '0.00',
            item.netQuantity?.toLocaleString() || '0',
          ]);
        });
      }
    });

    const filename = `${selectedCostCenter.name}-users-${
      monthNames[selectedCostCenter.month - 1]
    }-${selectedCostCenter.year}.csv`;
    generateCSV(headers, rows, filename);
  };

  const onRowsPerPageChange = (newRowsPerPage: number) =>
    setRowsPerPage(newRowsPerPage);

  const onPageChange = (newPage: number) => setUsersPage(newPage);

  return (
    <div className="cost-center-detail-section cost-center-detail-section--users">
      <div className="cost-center-section-heading">
        <div>
          <h2>
            Users for {monthNames[selectedCostCenter.month - 1]}{' '}
            {selectedCostCenter.year}
          </h2>
          <p>Current period user consumption for the selected cost center.</p>
        </div>
        <div className="cost-center-users-actions">
          {/* <label
            className="cost-centers-search cost-center-users-search"
            aria-label="Search users"
          >
            <input type="search" placeholder="Search by username or email" />
          </label> */}
          <EmeraldSearch
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            onClear={() => setSearchQuery('')}
            placeholder="Search by username or email"
            aria-label="Search users"
            disabled={isLoading}
            className="cost-centers-search-field-custom"
          />
          <div
            className="cost-centers-filter-dropdown"
            ref={usersFiltersDropdownRef}
          >
            <Button
              className={`cost-centers-filter-button ${
                showUsersFilters || activeUserFilterCount > 0 ? 'is-active' : ''
              }`}
              onClick={onToggleShowUsersFilters}
              aria-expanded={showUsersFilters}
              disabled={isLoading}
            >
              Filters
              <span className="cost-centers-filter-button__count">
                {activeUserFilterCount || 0}
              </span>
            </Button>

            {showUsersFilters ? (
              <div className="cost-centers-advanced-panel">
                <div className="cost-centers-advanced-panel__header">
                  <span>Filters</span>
                  <button
                    type="button"
                    className="cost-centers-clear-all"
                    onClick={onClearUserFilters}
                  >
                    Clear all
                  </button>
                </div>
                {/* <label className="cost-centers-checkbox">
                  <input
                    type="checkbox"
                    checked={userTableFilters.noCostCenter}
                    onChange={() => onToggleUserTableFilter('noCostCenter')}
                  />
                  <span>Users not in any cost center</span>
                </label> */}
                <label className="cost-centers-checkbox">
                  <input
                    type="checkbox"
                    checked={userTableFilters.activeNoCostCenter}
                    onChange={() =>
                      onToggleUserTableFilter('activeNoCostCenter')
                    }
                  />
                  <span>Active users with no cost center</span>
                </label>
                {/* <label className="cost-centers-checkbox">
                  <input
                    type="checkbox"
                    checked={userTableFilters.bothSources}
                    onChange={() => onToggleUserTableFilter('bothSources')}
                  />
                  <span>Users in both CBRE-EMU & CBRE-ENT</span>
                </label> */}
                <label className="cost-centers-checkbox">
                  <input
                    type="checkbox"
                    checked={userTableFilters.noEmail}
                    onChange={() => onToggleUserTableFilter('noEmail')}
                  />
                  <span>Users with no email</span>
                </label>
                <label className="cost-centers-checkbox">
                  <input
                    type="checkbox"
                    checked={userTableFilters.hideNoUsage}
                    onChange={() => onToggleUserTableFilter('hideNoUsage')}
                  />
                  <span>Hide users with no usage</span>
                </label>
              </div>
            ) : null}
          </div>
          <Button
            variant="contained"
            color="primary"
            size="small"
            onClick={handleExportCSV}
            style={{ marginLeft: '8px' }}
            disabled={isLoading}
          >
            Export CSV
          </Button>
        </div>
      </div>

      {isLoading ? (
        <SkeletonLoader />
      ) : (
        <>
          <div className="cost-center-summary-grid cost-center-summary-grid--detail-users">
            <div className="cost-center-summary-card">
              <span>Total users</span>
              <strong>{filteredUsersData?.usersData?.length || 0}</strong>
            </div>
            <div className="cost-center-summary-card cost-center-summary-card--danger">
              <span>Total excess spend</span>
              <strong>
                {formatCurrencyAbbreviated(
                  filteredUsersData?.additionalInfo?.totalNetAmount || 0,
                )}
              </strong>
            </div>
            <div className="cost-center-summary-card cost-center-summary-card--accent">
              <span>Total actual spend</span>
              <strong>
                {formatCurrencyAbbreviated(
                  filteredUsersData?.additionalInfo?.totalGrossAmount || 0,
                )}
              </strong>
            </div>
          </div>

          <div className="cost-center-table-shell">
            <table className="cost-center-table">
              <thead>
                <tr>
                  <th style={{ width: '40px' }}></th>
                  <th
                    onClick={() => handleSort('username')}
                    style={{ cursor: 'pointer', userSelect: 'none' }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      Username
                      {renderSortIcon('username')}
                    </div>
                  </th>
                  <th
                    onClick={() => handleSort('email')}
                    style={{ cursor: 'pointer', userSelect: 'none' }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      Email
                      {renderSortIcon('email')}
                    </div>
                  </th>
                  <th
                    onClick={() => handleSort('excessSpend')}
                    style={{ cursor: 'pointer', userSelect: 'none' }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      Excess spend ($)
                      {renderSortIcon('excessSpend')}
                    </div>
                  </th>
                  <th
                    onClick={() => handleSort('actualSpend')}
                    style={{ cursor: 'pointer', userSelect: 'none' }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      Actual spend ($)
                      {renderSortIcon('actualSpend')}
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody>
                {filteredUsersData?.usersData?.length > 0 ? (
                  currentPageData.map((user, idx) => {
                    const isExpanded = expandedRows.has(idx);

                    return (
                      <React.Fragment key={idx}>
                        <tr className="expandable-row">
                          <td style={{ padding: '8px' }}>
                            {user.usageItems?.length > 0 && (
                              <button
                                className="expand-button"
                                onClick={() => toggleRow(idx)}
                                aria-label={
                                  isExpanded ? 'Collapse row' : 'Expand row'
                                }
                              >
                                {isExpanded ? (
                                  <KeyboardArrowDown
                                    style={{ fontSize: '20px' }}
                                  />
                                ) : (
                                  <KeyboardArrowRight
                                    style={{ fontSize: '20px' }}
                                  />
                                )}
                              </button>
                            )}
                          </td>
                          <td>{user.user}</td>
                          <td>{user.email}</td>
                          <td
                            className="cost-center-overuse-value"
                            title={`$${user.totalNetAmount || 0}`}
                          >
                            {formatCurrencyAbbreviated(
                              user.totalNetAmount || 0,
                            )}
                          </td>
                          <td title={`$${user.totalGrossAmount || 0}`}>
                            {formatCurrencyAbbreviated(
                              user.totalGrossAmount || 0,
                            )}
                          </td>
                        </tr>
                        {user.usageItems?.length > 0 && (
                          <tr
                            className={`expandable-content ${
                              isExpanded ? 'expanded' : ''
                            }`}
                            style={{
                              maxHeight: isExpanded ? '500px' : '0',
                              opacity: isExpanded ? 1 : 0,
                              transition:
                                'max-height 0.3s ease, opacity 0.2s ease',
                              overflow: 'hidden',
                            }}
                          >
                            <td
                              colSpan={5}
                              style={{
                                padding: isExpanded ? '16px' : '0 16px',
                                border: 'none',
                              }}
                            >
                              <div
                                className="inner-table-bg"
                                style={{
                                  padding: '16px',
                                  borderRadius: '4px',
                                  display: isExpanded ? 'block' : 'none',
                                }}
                              >
                                <h4
                                  style={{
                                    margin: '0 0 12px 0',
                                    fontSize: '14px',
                                    fontWeight: 600,
                                  }}
                                >
                                  Usage Details
                                </h4>
                                <table
                                  className="cost-center-table"
                                  style={{
                                    width: '100%',
                                    background: 'white',
                                    fontSize: '13px',
                                  }}
                                >
                                  <thead>
                                    <tr>
                                      <th>Model</th>
                                      <th>Utilised AIC</th>
                                      <th>Actual Spend</th>
                                      <th>Excess Spend</th>
                                      <th>Overuse AIC</th>
                                      {/* <th>Discount Amount</th>
                                        <th>Discount Quantity</th> */}
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {user.usageItems.map((item, subIdx) => (
                                      <tr key={subIdx}>
                                        <td>{item.model}</td>
                                        <td
                                          title={item.grossQuantity.toLocaleString()}
                                        >
                                          {formatNumber(item.grossQuantity)}
                                        </td>
                                        <td
                                          title={`$${item.grossAmount.toLocaleString()}`}
                                        >
                                          {formatCurrencyAbbreviated(
                                            item.grossAmount,
                                          )}
                                        </td>
                                        <td
                                          title={`$${item.netAmount.toLocaleString()}`}
                                        >
                                          {formatCurrencyAbbreviated(
                                            item.netAmount,
                                          )}
                                        </td>
                                        <td
                                          title={item.netQuantity.toLocaleString()}
                                        >
                                          {formatNumber(item.netQuantity)}
                                        </td>
                                        {/* <td
                                            title={`$${item.discountAmount.toLocaleString()}`}
                                          >
                                            {formatCurrencyAbbreviated(
                                              item.discountAmount,
                                            )}
                                          </td>
                                          <td
                                            title={item.discountQuantity.toLocaleString()}
                                          >
                                            {formatNumber(
                                              item.discountQuantity,
                                            )}
                                          </td> */}
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })
                ) : (
                  <tr>
                    <td
                      colSpan={5}
                      style={{ textAlign: 'center', padding: '20px' }}
                    >
                      No users found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <Pagination
            page={usersPage}
            rowsPerPage={rowsPerPage}
            count={filteredUsersData?.usersData?.length || 0}
            onPageChange={onPageChange}
            onRowsPerPageChange={onRowsPerPageChange}
          />
        </>
      )}
    </div>
  );
};
